import React, {
  FC,
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames/dedupe";
import { DropdownContext } from "./Dropdown.context";
import {
  DropdownAlignment,
  DropdownDrawingStrategy,
  DropdownPosition,
  DropdownToggleStrategy,
  IDropdownProps,
  RectPos,
  TSelectButtonType,
} from "./Dropdown.interface";
import DynamicPortal from "../DynamicPortal/DynamicPortal";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import useTheme from "../ThemeProvider/useTheme";
import ThemeProvider from "../ThemeProvider/ThemeProvider";
import { noop } from "../../../helpers";
import { useWatchState } from "../../../hooks";

const INLINE_BUTTON_TYPES: TSelectButtonType[] = ["rounded", "clear"];

const Dropdown: FC<IDropdownProps> = ({
  id,
  variant,
  classes,
  disabled,
  toggleToFlyoutOnMobile = true,
  theme: propsTheme = "dark",
  buttonTheme: propsButtonTheme = "light",
  position = DropdownPosition.BOTTOM,
  alignment = DropdownAlignment.FIT,
  dropdownButton,
  opened = false,
  toggleStrategy = DropdownToggleStrategy.CLICK,
  drawingStrategy = DropdownDrawingStrategy.JS,
  onChange,
  onClick,
  onMouseEnter,
  onMouseLeave,
  children,
}) => {
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const [currentAlignment, setCurrentAlignment] = useState(alignment);
  const [currentPosition, setCurrentPosition] = useState(position);
  const [pos, setPos] = useState<RectPos>({});

  const [isOpened, setIsOpened] = useWatchState({ value: opened });

  const theme = useTheme(propsTheme);
  const buttonTheme = useTheme(propsButtonTheme);

  const handleClick = useCallback(
    (event) => {
      if (
        !disabled &&
        toggleStrategy === DropdownToggleStrategy.CLICK &&
        buttonRef.current &&
        buttonRef.current.contains(event.target)
      ) {
        setIsOpened(!isOpened);
      }
      onClick && onClick();
    },
    [disabled, isOpened, onClick, setIsOpened, toggleStrategy],
  );

  const handleMouseEnter = useCallback(() => {
    if (toggleStrategy === DropdownToggleStrategy.HOVER) {
      !disabled && setIsOpened(true);
    }
    onMouseEnter && onMouseEnter();
  }, [disabled, onMouseEnter, setIsOpened, toggleStrategy]);

  const handleMouseLeave = useCallback(() => {
    if (toggleStrategy === DropdownToggleStrategy.HOVER) {
      !disabled && setIsOpened(false);
    }
    onMouseLeave && onMouseLeave();
  }, [disabled, onMouseLeave, setIsOpened, toggleStrategy]);

  useUpdateEffect(() => {
    onChange && onChange(isOpened);
  }, [isOpened, onChange]);

  const Wrapper =
    drawingStrategy === DropdownDrawingStrategy.CSS ? "div" : Fragment;

  const wrapperProps = useMemo(() => {
    if (drawingStrategy === DropdownDrawingStrategy.CSS) {
      return {
        "data-testid": "Dropdown__wrapper",
        className: classNames(
          "dropdown__root uds-animation-root position-relative d-inline-block",
          classes?.wrapper,
        ),
        onClick: handleClick,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      };
    }
    return {};
  }, [
    drawingStrategy,
    classes,
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
  ]);

  const dropdownContext = useMemo(
    () => ({
      buttonRef,
      listRef,
      opened: isOpened,
      position,
      alignment,
      drawingStrategy,
      currentAlignment,
      currentPosition,
      theme,
      buttonTheme,
      toggleToFlyoutOnMobile,
      pos,
      setPos,
      setOpened: setIsOpened,
      setCurrentAlignment,
      setCurrentPosition,
    }),
    [
      alignment,
      buttonTheme,
      currentAlignment,
      currentPosition,
      drawingStrategy,
      isOpened,
      pos,
      position,
      setIsOpened,
      theme,
      toggleToFlyoutOnMobile,
    ],
  );

  return (
    <DropdownContext.Provider value={dropdownContext}>
      <Wrapper {...wrapperProps}>
        <div
          className={classNames(
            "dropdown__button",
            {
              "dropdown__button--inline": INLINE_BUTTON_TYPES.includes(variant),
            },
            classes?.button,
          )}
          ref={buttonRef}
          onClick={
            drawingStrategy === DropdownDrawingStrategy.JS ? handleClick : noop
          }
          onMouseEnter={
            drawingStrategy === DropdownDrawingStrategy.JS
              ? handleMouseEnter
              : noop
          }
          onMouseLeave={
            drawingStrategy === DropdownDrawingStrategy.JS
              ? handleMouseLeave
              : noop
          }
          data-testid="dropdown__button"
        >
          <ThemeProvider theme={buttonTheme}>{dropdownButton}</ThemeProvider>
          {drawingStrategy === DropdownDrawingStrategy.JS && (
            <DynamicPortal id={id} className="dropdown-zero-pos">
              {children}
            </DynamicPortal>
          )}
        </div>
        {drawingStrategy === DropdownDrawingStrategy.CSS && children}
      </Wrapper>
    </DropdownContext.Provider>
  );
};

export default Dropdown;
